import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { setPreventUnload } from 'flow/actions/app-actions';
import {
    archiveSession,
    markChatMessageRead,
    markChatSessionUnread,
    unArchiveSession,
} from 'flow/actions/chat-actions';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { ChatMember, ChatSession } from 'flow/types/Chats';
import { Extension } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import {
    getFixedNumberFromContact,
    getMobileNumberFromContact,
    getTechnicalNumberFromContact,
} from 'flow/utils/extension-utils';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapabilityVideo1on1,
} from 'flow/selectors/ExtensionSelectors';
import GenerateSummaryMenuItem from './GenerateSummaryMenuItem';
import VideoCallButtonMenuItem from './VideoCallButtonMenuItem';
import MuteSessionMenuItem from './MuteSessionMenuItem';
import CallButtonMenuItem from './CallButtonMenuItem';

interface Props {
    session: TypedMap<ChatSession>;
}

const SessionsListContextMenu = ({ session }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pathname } = useLocation();

    const ownUserHasVideo1on1Capability = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );
    const { isOwnChatUser } = useExtension();
    const { hasFeature } = useFeatureFlags();

    const hasUnreadMessages = session.get('numberOfUnreadMessages') > 0;

    const handleUnread = (e: React.MouseEvent<Element, MouseEvent>) => {
        e.stopPropagation();

        if (hasUnreadMessages) {
            dispatch(
                markChatMessageRead(
                    session.get('key'),
                    session.getIn(['latestMessage', 'key']),
                ),
            );

            return;
        }

        if (
            pathname ===
            generatePath(routes['messages.id'], { id: session.get('key') })
        ) {
            history.push(routes['messages']);
        }

        dispatch(markChatSessionUnread(session.get('key')));
    };

    const handleArchive = (e: React.MouseEvent<Element, MouseEvent>) => {
        e.stopPropagation();

        if (session.get('chatArchived')) {
            dispatch(unArchiveSession(session.get('key')));
            return;
        }

        dispatch(archiveSession(session.get('key')));
    };

    const isChatRoom =
        session.get('isChatRoom') || session.get('members').size > 2;

    const otherPart = session
        .get('members')
        .find((member) => !isOwnChatUser(member));

    const contact = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            otherPart?.get('extensionKey'),
            'contact',
        ]),
    );

    const canMakeVideoCall =
        otherPart &&
        ownUserHasVideo1on1Capability &&
        hasFeature(FeatureFlags.VIDEO_CANVAS);

    const isAISummarizedEnabled = hasFeature(
        FeatureFlags.AI_SUMMARIZE_UNREAD_MESSAGES,
    );

    return (
        <>
            {otherPart &&
                (isChatRoom ? (
                    <MuteSessionMenuItem session={session} />
                ) : (
                    <>
                        <CallButtonMenuItem
                            fixedNumber={getFixedNumberFromContact(contact)}
                            technicalNumber={getTechnicalNumberFromContact(
                                contact,
                            )}
                            mobileNumber={getMobileNumberFromContact(contact)}
                            extensionToCall={otherPart}
                        />

                        {canMakeVideoCall && (
                            <VideoCallButtonMenuItem
                                extensionKey={otherPart.get('extensionKey')}
                            />
                        )}

                        <MenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                history.push(
                                    generatePath(
                                        routes['extensions.entityKey.contact'],
                                        {
                                            entityKey:
                                                otherPart.get('extensionKey'),
                                        },
                                    ),
                                );
                            }}
                        >
                            {i18next.t('Chat.Details.GoToContactCard')}
                        </MenuItem>
                    </>
                ))}

            <MenuItem onClick={(e) => handleUnread(e)}>
                {hasUnreadMessages
                    ? i18next.t('chat.session.action.read')
                    : i18next.t('chat.session.action.unread')}
            </MenuItem>

            {isAISummarizedEnabled && (
                <GenerateSummaryMenuItem session={session} />
            )}

            <MenuItem onClick={(e) => handleArchive(e)}>
                {session.get('chatArchived')
                    ? i18next.t('chatsetting.unarchive')
                    : i18next.t('chatsetting.archive')}
            </MenuItem>

            {!isChatRoom && <SendEmailItem session={session} />}
        </>
    );
};

interface SendEmailItemProps {
    session: TypedMap<ChatSession>;
}

const SendEmailItem = ({ session }: SendEmailItemProps) => {
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const participant: TypedMap<ChatMember> = session
        .get('members')
        .find((m) => m.get('extensionKey') !== ownExtensionKey);

    const extension: TypedMap<Extension> | null = useAppSelector((state) =>
        participant
            ? state.extensions.getIn([
                  'extensions',
                  participant.get('extensionKey'),
              ])
            : null,
    );

    if (!extension) {
        return null;
    }

    return (
        <a
            href={`mailto:${extension.getIn(['contact', 'email'])}`}
            className="no-underline"
        >
            <MenuItem
                onClick={(e) => {
                    e.stopPropagation();
                    setPreventUnload(true);
                }}
            >
                {i18next.t('Tooltip.Title.SendEmail')}
            </MenuItem>
        </a>
    );
};

export default SessionsListContextMenu;
